
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Briefcase, UserRound } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';

const Onboarding = () => {
  const { user, profile, isLoading, updateUserRole } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'job_seeker' | 'recruiter' | null>(null);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      
      if (profile.onboarding_completed) {
        if (profile.role === 'job_seeker') {
          navigate('/job-seeker-dashboard');
        } else if (profile.role === 'recruiter') {
          navigate('/recruiter-dashboard');
        }
      }
    }
  }, [profile, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-forest">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  const validateEmail = async (email: string, role: 'job_seeker' | 'recruiter') => {
    if (role === 'recruiter') {
      const domain = email.split('@')[1];
      if (!domain) return false;
      
      const { data } = await supabase
        .from('organization_domains')
        .select('*')
        .eq('domain', domain)
        .single();
        
      return !!data;
    }
    
    return true; // Always valid for job seekers
  };

  const handleRoleSelection = async (selectedRole: 'job_seeker' | 'recruiter') => {
    try {
      if (!user.email) {
        throw new Error('No email found for user');
      }
      
      const isValid = await validateEmail(user.email, selectedRole);
      
      if (selectedRole === 'recruiter' && !isValid) {
        toast({
          title: "Organization Email Required",
          description: "Recruiters must use an organization email. Please contact support or use a job seeker account.",
          variant: "destructive",
        });
        return;
      }
      
      setRole(selectedRole);
      setStep(2);
    } catch (error) {
      console.error('Error validating email:', error);
      toast({
        title: "Error",
        description: "There was a problem with role selection. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!role || !firstName || !lastName) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Update user role
      await updateUserRole(role);
      
      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
        
      if (error) throw error;
      
      // Create profile for the selected role
      if (role === 'job_seeker') {
        const { error: jsError } = await supabase
          .from('job_seeker_profiles')
          .insert({ id: user.id });
          
        if (jsError) throw jsError;
        
        navigate('/job-seeker-dashboard');
      } else if (role === 'recruiter') {
        const { error: recError } = await supabase
          .from('recruiter_profiles')
          .insert({ id: user.id });
          
        if (recError) throw recError;
        
        navigate('/recruiter-dashboard');
      }
      
      toast({
        title: "Onboarding completed",
        description: "Your profile has been created successfully!",
      });
      
    } catch (error: any) {
      console.error('Error completing onboarding:', error);
      toast({
        title: "Error",
        description: error.message || "There was a problem completing onboarding.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-forest">
      <Card className="w-full max-w-lg bg-forest-light border border-mint/20 text-white">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Welcome to Skhy AI Consultancy</CardTitle>
          <CardDescription className="text-white/70">
            {step === 1 ? "Let's set up your account. Select your role:" : "Just a few more details to complete your profile:"}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {step === 1 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                className={`h-auto p-6 flex flex-col items-center space-y-4 text-white border-mint/30 hover:bg-mint/10`}
                onClick={() => handleRoleSelection('job_seeker')}
              >
                <UserRound size={48} className="text-mint" />
                <div className="text-center">
                  <h3 className="font-medium text-lg mb-2">Job Seeker</h3>
                  <p className="text-sm text-white/70">I'm looking for job opportunities and want to showcase my skills</p>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className={`h-auto p-6 flex flex-col items-center space-y-4 text-white border-mint/30 hover:bg-mint/10`}
                onClick={() => handleRoleSelection('recruiter')}
              >
                <Briefcase size={48} className="text-mint" />
                <div className="text-center">
                  <h3 className="font-medium text-lg mb-2">Recruiter</h3>
                  <p className="text-sm text-white/70">I want to find and connect with talented professionals</p>
                </div>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="first-name" className="text-sm font-medium">First Name</label>
                  <Input
                    id="first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    required
                    className="bg-forest border-mint/30 text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="last-name" className="text-sm font-medium">Last Name</label>
                  <Input
                    id="last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    required
                    className="bg-forest border-mint/30 text-white"
                  />
                </div>
              </div>
              
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full bg-mint text-forest hover:bg-mint/90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating profile...' : 'Complete Profile Setup'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
        
        {step === 2 && (
          <CardFooter>
            <Button 
              variant="ghost" 
              onClick={() => setStep(1)}
              className="w-full text-white hover:bg-mint/10"
            >
              Go Back
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default Onboarding;
